import { useEffect, useState } from "react";
import {
  UICircularProgress,
  UIDialog,
  UIDialogActions,
  UIDialogContent,
  UIDialogContentText,
  UIDialogTitle,
  UIIconButton,
  UIListItemIcon,
  UIListItemText,
  UIMenu,
  UIMenuItem,
  UITooltip
} from "../../_core/components";
import {
  Email as EmailIcon,
  MoreVert as MoreVertIcon,
  RemoveModerator as RemoveModeratorIcon,
  UnsubscribeOutlined as UnsubscribeIcon
} from "../../_core/components/icons";
import { UserLite } from "../api/types";
import { useDisableMfa, useRemoveCustomerUserEmailSuppression, useResendWelcomeEmail } from "../hooks";
import { useApiAlerts } from "../../_core/hooks";
import { useQueryClient } from "@tanstack/react-query";
import UIButton from "../../_core/components/ui/UIButton";
import RemoveEmailSuppressionDialog from "../../_core/components/ui/RemoveEmailSuppressionDialog";
import { isEmailSuppressed } from "../../_core/utils/emailDelivery";

export interface ActionsDropdownProps {
  user: UserLite;
  accountId: string;
}

const ActionsDropdown = ({
  user,
  accountId,
}: ActionsDropdownProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isMfaDialogOpen, setIsMfaDialogOpen] = useState(false);
  const [isSuppressionDialogOpen, setIsSuppressionDialogOpen] = useState(false);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();

  const {
    mutate: executeResendEmail,
    isSuccess: isEmailSuccess,
    error: emailError,
    isError: isEmailError,
    isPending: isEmailLoading,
  } = useResendWelcomeEmail();

  const {
    mutate: executeDisableMfa,
    isSuccess: isMfaSuccess,
    error: mfaError,
    isError: isMfaError,
    isPending: isMfaLoading,
  } = useDisableMfa();

  const {
    mutate: executeRemoveSuppression,
    isSuccess: isSuppressionSuccess,
    error: suppressionError,
    isError: isSuppressionError,
    isPending: isSuppressionLoading,
  } = useRemoveCustomerUserEmailSuppression();

  const getSuccessMessage = () => {
    if (isMfaSuccess) return "MFA disabled successfully";
    if (isEmailSuccess) return "Welcome email sent";
    if (isSuppressionSuccess) return "Email address removed from the suppression list";

    return undefined;
  };

  useApiAlerts(
    getSuccessMessage(),
    emailError || mfaError || suppressionError,
    isEmailError || isMfaError || isSuppressionError
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleResendWelcomeEmail = () => {
    executeResendEmail({ userId: user.id, accountId });
    // Don't close menu immediately - let it close after success/error
  };

  const handleDisableMfa = () => {
    setIsMfaDialogOpen(true);
    handleClose();
  };

  const handleCloseMfaDialog = () => {
    setIsMfaDialogOpen(false);
  };

  const handleConfirmDisableMfa = () => {
    executeDisableMfa(user.id);
  };

  const handleRemoveSuppression = () => {
    setIsSuppressionDialogOpen(true);
    handleClose();
  };

  const handleCloseSuppressionDialog = () => {
    if (!isSuppressionLoading) setIsSuppressionDialogOpen(false);
  };

  const handleConfirmRemoveSuppression = () => {
    executeRemoveSuppression(user.id);
  };

  useEffect(() => {
    if (isMfaSuccess) {
      setIsMfaDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["users"] }).then();
    }
  }, [isMfaSuccess, queryClient]);

  useEffect(() => {
    if (isSuppressionSuccess || isSuppressionError) {
      setIsSuppressionDialogOpen(false);
    }
  }, [isSuppressionSuccess, isSuppressionError]);

  useEffect(() => {
    if (isEmailSuccess || isEmailError) {
      handleClose();
    }
  }, [isEmailSuccess, isEmailError]);

  // Check if there are any additional actions available
  const isSuppressed = isEmailSuppressed(user.emailDeliveryFailure);
  const hasResendEmail = !user.validated;
  const hasMfaEnabled = user.mfaEnabled;
  const hasRemoveSuppression = isSuppressed;
  const hasAdditionalActions = hasResendEmail || hasMfaEnabled || hasRemoveSuppression;

  // If no additional actions, don't render the dropdown
  if (!hasAdditionalActions) {
    return null;
  }

  const resendMenuItem = (
    <UIMenuItem
      onClick={handleResendWelcomeEmail}
      disabled={isEmailLoading || isSuppressed}
      dense
    >
      <UIListItemIcon>
        {isEmailLoading ? (
          <UICircularProgress size={16} />
        ) : (
          <EmailIcon fontSize="small" />
        )}
      </UIListItemIcon>
      <UIListItemText
        primary="Resend Welcome Email"
      />
    </UIMenuItem>
  );

  return (
    <>
      <UITooltip title="Actions">
        <UIIconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? "actions-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <MoreVertIcon fontSize="small" />
        </UIIconButton>
      </UITooltip>

      <UIMenu
        id="actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "actions-button",
          dense: true,
        }}
      >
        {hasResendEmail && isSuppressed && (
          <UITooltip title="Remove the address from the suppression list first" placement="left">
            <span>{resendMenuItem}</span>
          </UITooltip>
        )}
        {hasResendEmail && !isSuppressed && resendMenuItem}

        {hasRemoveSuppression && (
          <UIMenuItem onClick={handleRemoveSuppression} dense>
            <UIListItemIcon>
              <UnsubscribeIcon fontSize="small" />
            </UIListItemIcon>
            <UIListItemText
              primary="Remove from Suppression List"
            />
          </UIMenuItem>
        )}

        {hasMfaEnabled && (
          <UIMenuItem onClick={handleDisableMfa} dense>
            <UIListItemIcon>
              <RemoveModeratorIcon fontSize="small" />
            </UIListItemIcon>
            <UIListItemText
              primary="Disable MFA"
            />
          </UIMenuItem>
        )}
      </UIMenu>

      <RemoveEmailSuppressionDialog
        open={isSuppressionDialogOpen}
        failure={user.emailDeliveryFailure}
        userName={user.name}
        isLoading={isSuppressionLoading}
        onConfirm={handleConfirmRemoveSuppression}
        onCancel={handleCloseSuppressionDialog}
      />

      <UIDialog
        open={isMfaDialogOpen}
        onClose={handleCloseMfaDialog}
        aria-labelledby="confirm-mfa-removal-title"
        aria-describedby="confirm-mfa-removal-description"
      >
        <UIDialogTitle id="confirm-mfa-removal-title">
          Disable Multi-Factor Authentication
        </UIDialogTitle>
        <UIDialogContent>
          <UIDialogContentText id="confirm-mfa-removal-description">
            Disabling two-factor authentication will reduce security for this account and increase vulnerability to unauthorized access. User <strong>{user.name}</strong> will need to reconfigure MFA to restore this protection.
          </UIDialogContentText>
        </UIDialogContent>
        <UIDialogActions>
          <UIButton
            onClick={handleCloseMfaDialog}
            variant={"outlined"}
            disabled={isMfaLoading}
          >
            Keep Enabled
          </UIButton>
          <UIButton
            onClick={handleConfirmDisableMfa}
            disabled={isMfaLoading}
            startIcon={isMfaLoading ? <UICircularProgress size={18} /> : undefined}
          >
            Disable
          </UIButton>
        </UIDialogActions>
      </UIDialog>
    </>
  );
};

export default ActionsDropdown;
